import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import s from '~/app/components/pages/auditor/clients/client/reports/styles.module.scss';
import { formatDate } from '~/app/utils/format-data';
import cx from '~/data/utils/helpers/cx';
import { type ValidationChecksComparisonRepresentation, ValidationComparisonStatus } from '~/data/openapi-client/index';
import SvgIcon from '@mui/material/SvgIcon';
import DownloadIcon from '@mui/icons-material/Download';
import TableRowsLoader from '~/ui/components/mui-table/table-loader';
import { toast } from 'react-toastify';

export type ComparisonTableProps = {
  comparisons: ValidationChecksComparisonRepresentation[];
  isSuccess: boolean;
  limit: number;
  downloadComparison: (id: string) => Promise<void>;
};

export function ComparisonTable({ comparisons, isSuccess, limit, downloadComparison }: ComparisonTableProps) {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="20%" key="date">
              <Typography fontSize={16} className="text-medium ml-8">
                Date
              </Typography>
            </TableCell>
            <TableCell width="30%" key="left-file">
              <Typography fontSize={16} className="text-medium ml-8">
                Left File
              </Typography>
            </TableCell>
            <TableCell width="30%" key="right-file">
              <Typography fontSize={16} className="text-medium ml-8">
                Right File
              </Typography>
            </TableCell>
            <TableCell width="10%" key="right-file">
              <Typography fontSize={16} className="text-medium ml-8">
                Status
              </Typography>
            </TableCell>
            <TableCell width="10%" key="actions">
              <Typography fontSize={16} className="text-medium ml-8">
                Actions
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {comparisons.length === 0 && isSuccess && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography fontSize={16} className="ml-8">
                  no records found
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {isSuccess ? (
            comparisons?.map((comparison) => (
              <TableRow className={s.rowLink} key={comparison.id} hover={true}>
                <TableCell>
                  <Typography fontSize={16} className="text-medium ml-8">
                    {formatDate(comparison.inserted)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontSize={16} className="text-medium ml-8">
                    {comparison.leftFile.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontSize={16} className="text-medium ml-8">
                    {comparison.rightFile.name}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography fontSize={16} className="text-medium ml-8">
                    <Chip
                      label={comparison.status}
                      className={cx(
                        (() => {
                          if (comparison.status === ValidationComparisonStatus.Idle) return 'bg-grey';
                          if (comparison.status === ValidationComparisonStatus.Processing) return 'bg-primary-blue';
                          if (comparison.status === ValidationComparisonStatus.Success) return 'bg-status-success';
                          if (comparison.status === ValidationComparisonStatus.Error) return 'bg-status-error';
                          return 'bg-grey';
                        })(),
                        'text-white',
                      )}
                    />
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (comparison.status !== 'Success') {
                        return toast.error('Can download only successful results');
                      }
                      downloadComparison(comparison.id);
                    }}
                    size="small"
                    className="ml-6"
                    endIcon={<SvgIcon component={DownloadIcon} fontSize="inherit" />}
                  >
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRowsLoader rowsNum={limit} colsNum={5} />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
